
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { useBooking } from '@/contexts/BookingContext';
import { getAvailableTimeSlots } from '@/data/mockData';
import { motion } from 'framer-motion';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, Calendar as CalendarIcon, Upload, MessageCircle } from 'lucide-react';

const TimeStep = () => {
  const { 
    selectedProvider, 
    selectedService, 
    selectedDate, 
    setSelectedDate, 
    selectedTime, 
    setSelectedTime 
  } = useBooking();
  
  const [isLoading, setIsLoading] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  
  useEffect(() => {
    if (selectedDate) {
      setIsLoading(true);
      
      // Simulate API call delay
      setTimeout(() => {
        const slots = getAvailableTimeSlots(selectedDate, selectedProvider?.id || '');
        setAvailableTimeSlots(slots);
        setIsLoading(false);
      }, 600);
    }
  }, [selectedDate, selectedProvider?.id]);
  
  if (!selectedProvider || !selectedService) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="flex justify-center"
      >
        <Card className="glass-card p-8 text-center max-w-md">
          <p className="text-chrona-gray-400">Bitte wähle zuerst einen Anbieter und eine Leistung.</p>
        </Card>
      </motion.div>
    );
  }
  
  // Calculate date range: today to 2 months in the future
  const today = new Date();
  const twoMonthsLater = new Date();
  twoMonthsLater.setMonth(today.getMonth() + 2);
  
  // Handle file upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div variants={itemVariants}>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-1.5 rounded-full bg-chrona-accent/20">
              <CalendarIcon size={18} className="text-chrona-accent" />
            </div>
            <h2 className="font-medium text-lg text-chrona-text">Wähle ein Datum</h2>
          </div>
          <Card className="glass-card overflow-hidden">
            <CardContent className="p-4">
              <Calendar
                mode="single"
                selected={selectedDate || undefined}
                onSelect={(date) => {
                  if (date) {
                    setSelectedDate(date);
                    setSelectedTime(null);
                  }
                }}
                disabled={{ before: today, after: twoMonthsLater }}
                className="rounded-xl bg-transparent"
              />
            </CardContent>
          </Card>
        </motion.div>
        
        <motion.div variants={itemVariants}>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-1.5 rounded-full bg-chrona-accent/20">
              <Clock size={18} className="text-chrona-accent" />
            </div>
            <h2 className="font-medium text-lg text-chrona-text">Wähle eine Uhrzeit</h2>
          </div>
          
          {!selectedDate ? (
            <Card className="glass-card h-[300px] flex items-center justify-center">
              <CardContent className="p-6 text-center text-chrona-gray-400">
                <Clock size={40} className="mx-auto mb-4 text-chrona-gray-600" />
                Bitte wähle zuerst ein Datum.
              </CardContent>
            </Card>
          ) : (
            <Card className="glass-card overflow-hidden">
              <CardContent className="p-4">
                {isLoading ? (
                  <div className="grid grid-cols-3 gap-2">
                    {[...Array(9)].map((_, i) => (
                      <Skeleton 
                        key={i}
                        className="h-10 w-full bg-chrona-gray-800/60" 
                      />
                    ))}
                  </div>
                ) : availableTimeSlots.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {availableTimeSlots.map((time) => (
                      <motion.button
                        key={time}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.98 }}
                        className={`py-3 px-4 text-center rounded-xl border transition-all ${
                          selectedTime === time
                            ? 'bg-chrona-accent text-white border-chrona-accent shadow-md shadow-chrona-accent/20'
                            : 'bg-chrona-gray-800/60 hover:bg-chrona-gray-800 border-white/10 text-chrona-text'
                        }`}
                        onClick={() => setSelectedTime(time)}
                      >
                        {time}
                      </motion.button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 text-chrona-gray-400">
                    <Clock size={40} className="mx-auto mb-4 text-chrona-gray-600" />
                    Keine verfügbaren Zeiten für dieses Datum.
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </motion.div>
      </div>
      
      {selectedProvider.aktivierte_addons?.length > 0 && selectedDate && selectedTime && (
        <motion.div 
          className="mt-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="p-1.5 rounded-full bg-chrona-accent/20">
              <MessageCircle size={18} className="text-chrona-accent" />
            </div>
            <h2 className="font-medium text-lg text-chrona-text">Zusatzoptionen</h2>
          </div>
          <Card className="glass-card">
            <CardContent className="p-6">
              {selectedProvider.aktivierte_addons.includes('bildupload') && (
                <div className="mb-6">
                  <h3 className="text-base font-medium mb-2 text-chrona-text flex items-center gap-2">
                    <Upload size={16} className="text-chrona-accent" />
                    Referenzbild hochladen
                  </h3>
                  <p className="text-sm text-chrona-gray-400 mb-3">
                    Damit wir deine Wünsche besser verstehen können.
                  </p>
                  
                  {!uploadedImage ? (
                    <div className="border-2 border-dashed border-white/10 rounded-xl p-6 text-center hover:border-chrona-accent/50 transition-colors cursor-pointer bg-black/20">
                      <input 
                        type="file" 
                        className="hidden" 
                        id="image-upload" 
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                      <label htmlFor="image-upload" className="cursor-pointer">
                        <div className="mx-auto w-12 h-12 rounded-full bg-chrona-gray-800/60 flex items-center justify-center mb-3">
                          <Upload className="text-chrona-gray-400" size={20} />
                        </div>
                        <span className="text-sm text-chrona-gray-300 hover:text-chrona-accent">
                          Klicke hier, um ein Bild hochzuladen
                        </span>
                      </label>
                    </div>
                  ) : (
                    <div className="relative rounded-xl overflow-hidden">
                      <img 
                        src={uploadedImage} 
                        alt="Uploaded reference" 
                        className="w-full max-h-48 object-cover" 
                      />
                      <button
                        className="absolute top-2 right-2 bg-chrona-gray-900/80 p-1.5 rounded-full text-white hover:bg-chrona-accent transition-colors"
                        onClick={() => setUploadedImage(null)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {selectedProvider.aktivierte_addons.includes('fragebogen') && (
                <div>
                  <h3 className="text-base font-medium mb-2 text-chrona-text flex items-center gap-2">
                    <MessageCircle size={16} className="text-chrona-accent" />
                    Kurze Infos zu deinem Termin
                  </h3>
                  <textarea
                    className="w-full bg-black/20 border border-white/10 rounded-xl p-3 text-sm text-chrona-text placeholder:text-chrona-gray-500 focus:ring-2 focus:ring-chrona-accent/30 focus:border-chrona-accent/50 transition-all duration-300"
                    rows={4}
                    placeholder="Füge hier zusätzliche Informationen für deinen Termin ein..."
                  ></textarea>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}
    </motion.div>
  );
};

export default TimeStep;
